import { api } from '@/services/api/client';
import { CUSTOMER_FLAG_SLUGS } from './customerFlags';
import type {
  CustomerFlag,
  CustomerFlagAction,
  CustomerFlagPayload,
} from './customerFlags';
import type {
  AcceptOfferRequest,
  AdjustBalanceRequest,
  AdjustMaxLoanAmountRequest,
  AdminOffer,
  AdminOffersResponse,
  ApproveAssessmentRequest,
  AssessmentBankStatementRow,
  AssessmentDetail,
  AssessmentListParams,
  AssessmentListResponse,
  AssessmentsResponse,
  ChangePaymentScheduleRequest,
  CreditedReferral,
  Customer,
  CustomerListParams,
  CustomerListResponse,
  CustomerLogsParams,
  CustomerLogsResponse,
  CustomerReferralsResponse,
  DelayLoanPaymentRequest,
  EarlyPayoutState,
  EmploymentInfo,
  GenerateOfferRequest,
  HoldInterestRequest,
  LedgerEntry,
  Loan,
  LoansListParams,
  LoansListResponse,
  LoanTransaction,
  MarketingStatus,
  OfferType,
  PaymentMethod,
  RecordPaymentRequest,
  RejectAssessmentRequest,
  ResetPasswordChannel,
  ScheduledPayment,
  SendBankLinkRequest,
  UpdateAssessmentBlacklistRequest,
  UpdateCustomerProfileRequest,
  UpdateEmploymentInfoRequest,
  UpdatePaymentMethodRequest,
  WaiveChargesRequest,
} from './types';

function isWrappedResponse<T>(data: unknown): data is { data: T } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'data' in data &&
    typeof (data as Record<string, unknown>)['data'] === 'object'
  );
}

export const customerService = {
  getCustomers: async (
    params: CustomerListParams = {}
  ): Promise<CustomerListResponse> => {
    const search = params.search?.trim();
    const { status, duplicate, registeredStart, registeredEnd } = params;
    const response = await api.get<CustomerListResponse>('/customers', {
      params: {
        page: params.page ?? 1,
        itemsPerPage: params.itemsPerPage ?? 20,
        // BE caps the search param at 100 chars (ListCustomersRequest).
        ...(search ? { search: search.slice(0, 100) } : {}),
        // Server-side filters (BE #1238). An empty value is a 400, not
        // "all", so an unset filter stays off the query string entirely.
        ...(status ? { status } : {}),
        ...(duplicate !== undefined ? { duplicate } : {}),
        ...(registeredStart ? { registeredStart } : {}),
        ...(registeredEnd ? { registeredEnd } : {}),
      },
    });

    return isWrappedResponse<CustomerListResponse>(response.data)
      ? response.data['data']
      : response.data;
  },

  /**
   * Get customer details by ID
   * @param customerId - The customer ID to fetch
   * @returns Customer details
   */
  getCustomer: async (customerId: string): Promise<Customer> => {
    // Token is automatically added by the API client interceptor
    const response = await api.get<Customer>(`/customers/${customerId}`);

    // Handle both wrapped and unwrapped responses
    // If response.data has a 'data' property, it's wrapped in ApiResponse
    // Otherwise, response.data is the Customer directly
    type ResponseWrapper = { data?: Customer };
    const customerData =
      (response.data as ResponseWrapper).data || response.data;

    return customerData as Customer;
  },

  /**
   * Successful Referrals — GET /customers/{customerId}/referrals (Refer-a-Friend
   * spec 5.1). `customerId` is the REFERRER. Only credited referrals come back,
   * most recently paid first, unpaginated; pending ones have no award yet and
   * are omitted. A customer with none (or an unknown id) is `[]`, not a 404.
   */
  getCustomerReferrals: async (
    customerId: string
  ): Promise<CreditedReferral[]> => {
    const response = await api.get<CustomerReferralsResponse>(
      `/customers/${customerId}/referrals`
    );
    const body = isWrappedResponse<CustomerReferralsResponse>(response.data)
      ? response.data['data']
      : response.data;
    return body.referrals ?? [];
  },

  /**
   * Marketing Status (GET /customers/{customerId}/marketing-preferences, BE
   * PR #1341, MPN-145): `{ enabled }`, true while the customer is opted in on
   * any of SMS, email or push — read from Iterable, not the profile. 404
   * CUSTOMER_PROFILE_NOT_FOUND for an unknown customer.
   */
  getMarketingStatus: async (customerId: string): Promise<MarketingStatus> => {
    const response = await api.get<MarketingStatus>(
      `/customers/${customerId}/marketing-preferences`
    );
    return isWrappedResponse<MarketingStatus>(response.data)
      ? response.data['data']
      : response.data;
  },

  /**
   * PUT /customers/{customerId}/marketing-preferences `{ enabled }` — opts
   * the customer in to (true) or out of (false) marketing on SMS, email AND
   * push together; transactional comms are untouched. Staff bypass the
   * customer's marketing-consent lock (bankrupt / debt-agency flags) and the
   * consent audit rows name the staff member. Returns the resulting
   * `{ enabled }`. 400 INVALID_REQUEST, 404 CUSTOMER_PROFILE_NOT_FOUND.
   */
  setMarketingStatus: async (
    customerId: string,
    enabled: boolean
  ): Promise<MarketingStatus> => {
    const response = await api.put<MarketingStatus>(
      `/customers/${customerId}/marketing-preferences`,
      { enabled }
    );
    return isWrappedResponse<MarketingStatus>(response.data)
      ? response.data['data']
      : response.data;
  },

  /**
   * The customer's payment methods on file — the LIVE records
   * (GET /customers/{customerId}/payment-methods, BE PR #1175): a bare array
   * of the same shape a loan's `paymentMethod` carries, plus `isNextDayBank`
   * on bank accounts. A customer with none (or an unknown id) is `[]`, not a
   * 404. A loan's `paymentMethod` is that loan's snapshot and goes stale once
   * the account is replaced after the loan closes; credits and reprocessed
   * disbursements pay to the bank account in THIS list.
   */
  getCustomerPaymentMethods: async (
    customerId: string
  ): Promise<PaymentMethod[]> => {
    const response = await api.get<PaymentMethod[]>(
      `/customers/${customerId}/payment-methods`
    );
    const body = isWrappedResponse<PaymentMethod[]>(response.data)
      ? response.data['data']
      : response.data;
    return Array.isArray(body) ? body : [];
  },

  /**
   * Get loans for a specific customer
   * @param customerId - The customer ID to fetch loans for
   * @returns Array of loans
   */
  getLoans: async (customerId: string): Promise<Loan[]> => {
    // Token is automatically added by the API client interceptor
    const response = await api.get<Loan[]>(`/customers/${customerId}/loans`);

    // Handle both wrapped and unwrapped responses
    type ResponseWrapper = { data?: Loan[] };
    const loansData = (response.data as ResponseWrapper).data || response.data;

    return loansData as Loan[];
  },

  /**
   * Get a specific loan by customer ID and loan ID
   * @param customerId - The customer ID
   * @param loanId - The loan ID
   * @returns Loan details
   */
  getLoan: async (customerId: string, loanId: string): Promise<Loan> => {
    // Token is automatically added by the API client interceptor
    const response = await api.get<Loan>(
      `/customers/${customerId}/loans/${loanId}`
    );

    // Handle both wrapped and unwrapped responses
    type ResponseWrapper = { data?: Loan };
    const loanData = (response.data as ResponseWrapper).data || response.data;

    return loanData as Loan;
  },

  /**
   * Get scheduled payments for a specific loan
   * @param loanId - The loan ID
   * @param repaymentsOnly - If true, only returns repayments (negative amountDue)
   * @returns Array of scheduled payments
   */
  getScheduledPayments: async (
    loanId: string,
    repaymentsOnly: boolean = true
  ): Promise<ScheduledPayment[]> => {
    // Token is automatically added by the API client interceptor
    const response = await api.get<ScheduledPayment[]>(
      `/loans/${loanId}/scheduled-payments`,
      {
        params: { repaymentsOnly },
      }
    );

    // Handle both wrapped and unwrapped responses
    type ResponseWrapper = { data?: ScheduledPayment[] };
    const paymentsData =
      (response.data as ResponseWrapper).data || response.data;

    // Ensure we always return an array
    if (Array.isArray(paymentsData)) {
      return paymentsData;
    }

    // If it's not an array, return empty array
    return [];
  },

  /**
   * Get ledger entries for a specific loan
   * @param customerId - The customer ID
   * @param loanId - The loan ID
   * @returns Array of ledger entries
   */
  getLogs: async (
    customerId: string,
    params: CustomerLogsParams = {}
  ): Promise<CustomerLogsResponse> => {
    const page = params.page ?? 1;
    const itemsPerPage = params.itemsPerPage ?? 20;

    const response = await api.get<CustomerLogsResponse>(
      `/customers/${customerId}/logs`,
      { params: { page, itemsPerPage } }
    );

    return isWrappedResponse<CustomerLogsResponse>(response.data)
      ? response.data['data']
      : response.data;
  },

  getAssessments: async (customerId: string): Promise<AssessmentsResponse> => {
    const response = await api.get<AssessmentsResponse>(
      `/customers/${customerId}/assessments`
    );

    return isWrappedResponse<AssessmentsResponse>(response.data)
      ? response.data['data']
      : response.data;
  },

  getAssessmentDetail: async (
    assessmentId: string
  ): Promise<AssessmentDetail> => {
    const response = await api.get<AssessmentDetail>(
      `/assessments/${assessmentId}`
    );

    return isWrappedResponse<AssessmentDetail>(response.data)
      ? response.data['data']
      : response.data;
  },

  /**
   * GET /assessments (BE #1371, MPN-175): one page of every assessment across
   * all customers, newest first, filtered server-side. Only a set filter goes
   * on the wire — an empty status=, dateStart= or dateEnd= is a 400.
   */
  getAllAssessments: async (
    params: AssessmentListParams = {}
  ): Promise<AssessmentListResponse> => {
    const { status, dateStart, dateEnd } = params;
    const response = await api.get<AssessmentListResponse>('/assessments', {
      params: {
        page: params.page ?? 1,
        itemsPerPage: params.itemsPerPage ?? 20,
        ...(status ? { status } : {}),
        ...(dateStart ? { dateStart } : {}),
        ...(dateEnd ? { dateEnd } : {}),
      },
    });

    return isWrappedResponse<AssessmentListResponse>(response.data)
      ? response.data['data']
      : response.data;
  },

  /**
   * GET /customers/{customerId}/employment-info — the profile's current wage
   * frequency (source of truth), latest approved credit limit, nearest pending
   * repayment and the derived auto-approved flag. 404 when the customer does
   * not exist. Absent fields mean "none" (BE serialises with exclude_none).
   */
  getEmploymentInfo: async (customerId: string): Promise<EmploymentInfo> => {
    const response = await api.get<EmploymentInfo>(
      `/customers/${customerId}/employment-info`
    );

    return isWrappedResponse<EmploymentInfo>(response.data)
      ? response.data['data']
      : response.data;
  },

  /**
   * PATCH /customers/{customerId}/employment-info — 204 No Content. Rewrites
   * the profile's wage frequency and syncs it to the active loan(s), or to the
   * latest unused approved assessment when there is no active loan. 400 on
   * day-field/frequency mismatch, 409 INVALID_EMPLOYMENT_INFO_UPDATE when a
   * credit limit is sent with an active loan or no valid assessment exists.
   */
  updateEmploymentInfo: async (
    customerId: string,
    payload: UpdateEmploymentInfoRequest
  ): Promise<void> => {
    await api.patch(`/customers/${customerId}/employment-info`, payload);
  },

  /**
   * POST /customers/{customerId}/force-reassessment — 204. The body is
   * REQUIRED since BE PR #1235 (400 INVALID_REQUEST without it):
   * `{ forceReassessment: true }` expires the customer's unexpired loan-less
   * approved assessments and sets the profile flag (autoApproved reads false
   * until they complete a new manual assessment); `false` clears the flag
   * only — expired assessments stay expired. 404 CUSTOMER_PROFILE_NOT_FOUND.
   */
  forceReassessment: async (
    customerId: string,
    forceReassessment: boolean
  ): Promise<void> => {
    await api.post(`/customers/${customerId}/force-reassessment`, {
      forceReassessment,
    });
  },

  /**
   * Admin override — approve an assessment or adjust its max loan amount.
   * Always requires a reason. Returns 204 No Content on success. Since BE
   * PR #1064 every update 409s (ASSESSMENT_PRECONDITION_FAILED) while a loan
   * is active against the assessment — use updateEmploymentInfo instead.
   */
  patchAssessment: async (
    assessmentId: string,
    payload:
      | ApproveAssessmentRequest
      | AdjustMaxLoanAmountRequest
      | RejectAssessmentRequest
  ): Promise<void> => {
    await api.patch(`/assessments/${assessmentId}`, payload);
  },

  /**
   * Send a bank-statement link to the customer via the requested channels,
   * created with the given statement provider (required since BE #1000).
   * POST /assessments/{assessmentId}/bank-link — 201 with an empty body on
   * success; 400 MISSING_CONTACT when the customer has no mobile on file.
   */
  sendBankLink: async (
    assessmentId: string,
    payload: SendBankLinkRequest
  ): Promise<void> => {
    await api.post(`/assessments/${assessmentId}/bank-link`, payload);
  },

  /**
   * List the bank statements that have landed against an assessment.
   * GET /assessments/{assessmentId}/bank-statements — spec B.1. Returns a
   * bare array (no `ApiResponse` envelope), newest first. `200 []` means no
   * statement has landed, which is distinct from the `404` for a missing
   * assessment — and is specifically not "nothing was ever requested".
   */
  getAssessmentBankStatements: async (
    assessmentId: string
  ): Promise<AssessmentBankStatementRow[]> => {
    const response = await api.get<AssessmentBankStatementRow[]>(
      `/assessments/${assessmentId}/bank-statements`
    );

    return isWrappedResponse<AssessmentBankStatementRow[]>(response.data)
      ? response.data['data']
      : response.data;
  },

  /**
   * Download a statement's CreditSense report as a complete HTML document.
   * GET /assessments/{assessmentId}/bank-statements/{statementId}/report —
   * spec C.1, as amended by backend PR #952.
   *
   * The 200 body is the raw HTML, gzipped on the wire; axios decompresses it
   * transparently. `responseType: 'text'` is load-bearing twice over: it
   * stops axios's default `transformResponse` from attempting `JSON.parse`
   * on a multi-megabyte string, and it keeps the document out of the
   * envelope-unwrapping path above. Errors stay JSON, but arrive here as an
   * unparsed string for the same reason — so callers branch on HTTP status,
   * not `errorCode`, which is what the contract prescribes.
   *
   * Every 200 is audited as a report-view against the customer: never
   * prefetch this, and never retry it automatically on success.
   *
   * Large and slow: a real dev report measured 2026-08-17 came back in 6.9s
   * as 1.1 MB gzipped / 5.4 MB decoded, and the C.1 spec records documents
   * up to 4.3 MB. The 2-minute app-wide timeout clears API Gateway's ~30s
   * ceiling and the backend's 20s stall guard, so a slow export surfaces the
   * BE's 502 rather than a client abort — no per-request override needed.
   */
  getAssessmentBankStatementReport: async (
    assessmentId: string,
    statementId: string
  ): Promise<string> => {
    const response = await api.get<string>(
      `/assessments/${assessmentId}/bank-statements/${statementId}/report`,
      {
        responseType: 'text',
        headers: { Accept: 'text/html' },
      }
    );

    return response.data as unknown as string;
  },

  /**
   * Replace the customer's on-file bank account.
   * POST /customers/{customerId}/payment-method — 202 with no body: the
   * request only validates and queues; the account swap (and future-schedule
   * rebuild on every active loan) is applied asynchronously.
   * 422 INVALID_BANK_ACCOUNT (unknown BSB) / BANK_ACCOUNT_UNCHANGED.
   */
  updatePaymentMethod: async (
    customerId: string,
    payload: UpdatePaymentMethodRequest
  ): Promise<void> => {
    await api.post(`/customers/${customerId}/payment-method`, payload);
  },

  /**
   * Admin override — set the early-payout state for a customer: `allow`
   * (force-allow 24h), `block` (force-block 24h), or `normal` (system decides).
   * Allow and block are mutually exclusive; setting one clears the other.
   * Returns 204 No Content on success. Contract:
   * 2026-07-23-early-payout-three-state-override-fe-design.md §2.
   */
  setEarlyPayoutOverride: async (
    customerId: string,
    state: EarlyPayoutState
  ): Promise<void> => {
    await api.patch(`/customers/${customerId}/early-payout-override`, {
      state,
    });
  },

  /**
   * Admin edit of a customer's profile fields.
   * PATCH /customers/{customerId} — returns 204 No Content.
   *
   * Send only changed fields (omitted = untouched). `nickname: null` clears
   * it; userName/contactEmail/mobileNumber/address are set-only. Malformed or
   * in-use mobile → 400 INVALID_MOBILE_NUMBER; userName already on another
   * account → 409 EMAIL_ALREADY_TAKEN. Shares the route with
   * `setCoolOffExpiration` below. Contract:
   * 2026-07-20-customer-edit-details-reset-password-design.md §3.1.
   */
  updateProfile: async (
    customerId: string,
    payload: UpdateCustomerProfileRequest
  ): Promise<void> => {
    await api.patch(`/customers/${customerId}`, payload);
  },

  /**
   * Send the customer a password-reset link (static forgot-password page URL)
   * via the requested channels — sms → mobileNumber, email → contactEmail
   * unless `recipient` names another address (SCRUM-1444 AC9: any account,
   * e.g. the login email). `recipient` steers the EMAIL leg only; SMS always
   * goes to the number on file. An empty recipient is a 400, so omit it.
   * POST /customers/{customerId}/reset-password-link — 202 on success (best
   * effort: succeeds when at least one requested channel has a recipient);
   * 422 INVALID_RECIPIENT when none do.
   */
  sendPasswordResetLink: async (
    customerId: string,
    channels: ResetPasswordChannel[],
    recipient?: string
  ): Promise<void> => {
    await api.post(`/customers/${customerId}/reset-password-link`, {
      channels,
      ...(recipient ? { recipient } : {}),
    });
  },

  /**
   * Check availability/validity of a mobile number (E.164 AU: +614xxxxxxxx).
   * POST /customers/validate-mobile — the API returns the string "true"
   * (available) or "false" (invalid or already in use).
   */
  validateMobile: async (phoneNumber: string): Promise<boolean> => {
    const response = await api.post<string | boolean>(
      '/customers/validate-mobile',
      { phoneNumber }
    );
    const data: unknown = response.data;
    return data === 'true' || data === true;
  },

  /**
   * Check whether an email is free to become a customer's username (login).
   * POST /customers/validate-email — the API returns a bare status string:
   * "AVAILABLE", "ALREADY_TAKEN" or "LEGACY_*" (a pre-migration account owns
   * it). Only AVAILABLE passes: the PATCH rejects every other case with a 409.
   * The lookup is case-insensitive; under 5 / over 100 characters is a 400.
   */
  validateEmail: async (email: string): Promise<boolean> => {
    const response = await api.post<string>('/customers/validate-email', {
      email,
    });
    const data: unknown = response.data;
    return data === 'AVAILABLE';
  },

  /**
   * Set, extend, or clear a customer's collections cool-off. Pass an ISO
   * datetime to set/extend it, or `null` to clear it. Returns 204 No Content
   * on success. (Admin PATCH /customers/{customerId}.)
   */
  setCoolOffExpiration: async (
    customerId: string,
    expiration: string | null
  ): Promise<void> => {
    await api.patch(`/customers/${customerId}`, {
      coolOffExpiration: expiration,
    });
  },

  getAllLoans: async (
    params: LoansListParams = {}
  ): Promise<LoansListResponse> => {
    const { dateField, dateStart, dateEnd } = params;
    const windowed = Boolean(dateStart || dateEnd);
    const response = await api.get<LoansListResponse>('/loans', {
      params: {
        page: params.page ?? 1,
        itemsPerPage: params.itemsPerPage ?? 20,
        // Date window (BE #1240). dateField only means something next to a
        // bound, and an empty dateStart=/dateEnd= is a 400 — so the field
        // rides along only with a set side, and unset sides stay off.
        ...(windowed && dateField ? { dateField } : {}),
        ...(dateStart ? { dateStart } : {}),
        ...(dateEnd ? { dateEnd } : {}),
      },
    });

    return isWrappedResponse<LoansListResponse>(response.data)
      ? response.data['data']
      : response.data;
  },

  getLoanLogs: async (
    loanId: string,
    params: CustomerLogsParams = {}
  ): Promise<CustomerLogsResponse> => {
    const page = params.page ?? 1;
    const itemsPerPage = params.itemsPerPage ?? 20;

    const response = await api.get<CustomerLogsResponse>(
      `/loans/${loanId}/logs`,
      { params: { page, itemsPerPage } }
    );

    return isWrappedResponse<CustomerLogsResponse>(response.data)
      ? response.data['data']
      : response.data;
  },

  getLoanOffers: async (
    customerId: string,
    loanId: string
  ): Promise<AdminOffer[]> => {
    const response = await api.get<AdminOffersResponse>(
      `/customers/${customerId}/loans/${loanId}/offers`
    );

    const payload = isWrappedResponse<AdminOffersResponse>(response.data)
      ? response.data['data']
      : response.data;

    return Array.isArray(payload?.offers) ? payload.offers : [];
  },

  acceptOffer: async (
    offerId: string,
    payload: AcceptOfferRequest
  ): Promise<void> => {
    await api.post(`/offers/${offerId}/accept`, payload);
  },

  /**
   * Generate and send a fresh offer of `offerType` on a customer's loan
   * (SCRUM-1547). 202 Accepted with no body: a background consumer first
   * HARD-DELETES every existing offer record on the loan (any status —
   * generated, accepted, defaulted, completed), then regenerates the tier
   * through the normal generation path (audit log attributed to the admin,
   * Iterable new-offer comms). Two FIFO hops, so the new offer lands a few
   * seconds after the 202 — re-fetch GET .../offers to observe it. 404 when
   * the loan doesn't exist or belongs to another customer.
   */
  generateOffer: async (
    customerId: string,
    loanId: string,
    offerType: OfferType
  ): Promise<void> => {
    const body: GenerateOfferRequest = { offerType };
    await api.post(`/customers/${customerId}/loans/${loanId}/offers`, body);
  },

  getLedger: async (
    customerId: string,
    loanId: string
  ): Promise<LedgerEntry[]> => {
    const response = await api.get<LedgerEntry[]>(
      `/customers/${customerId}/loans/${loanId}/ledger`
    );

    // Endpoint returns a flat array, but handle wrapped responses too
    if (Array.isArray(response.data)) {
      return response.data;
    }

    type ResponseWrapper = { data?: LedgerEntry[] };
    const data = (response.data as ResponseWrapper).data;
    return Array.isArray(data) ? data : [];
  },

  /**
   * Replace payment schedule for a specific loan
   * @param loanId - The loan ID
   * @param payload - The schedule replacement request
   * @returns void (202 response, no body)
   */
  changePaymentSchedule: async (
    loanId: string,
    payload: ChangePaymentScheduleRequest
  ): Promise<void> => {
    await api.post(`/loans/${loanId}/replace-schedule`, payload);
  },

  adjustBalance: async (
    loanId: string,
    payload: AdjustBalanceRequest
  ): Promise<void> => {
    await api.post(`/loans/${loanId}/balance-adjustment`, payload);
  },

  /**
   * Waive charges already incurred (202 Accepted, async). Each charge is
   * credited as its own "Charge Waiver" ledger row; interest keeps running.
   */
  waiveCharges: async (
    loanId: string,
    payload: WaiveChargesRequest
  ): Promise<void> => {
    await api.post(`/loans/${loanId}/waive-charges`, payload);
  },

  /**
   * Stop future interest (202 Accepted, async): the remaining schedule is
   * rebuilt interest-free and nothing already charged is credited. Not
   * reversible; a repeat on an already-held advance is a silent no-op.
   */
  holdInterest: async (
    loanId: string,
    payload: HoldInterestRequest
  ): Promise<void> => {
    await api.post(`/loans/${loanId}/hold-interest`, payload);
  },

  /**
   * Pay a failed advance out again (202 Accepted, async) to the bank account on
   * file, for the advance's own amount. Rebuilds the repayment where the
   * failure removed it; leaves a kept one alone. No body: the actor comes from
   * the token and the amount from the advance. MPN-137.
   */
  reprocessDisbursement: async (loanId: string): Promise<void> => {
    await api.post(`/loans/${loanId}/reprocess-disbursement`);
  },

  /** Every transaction on a loan — the record-payment debit picker. */
  getLoanTransactions: async (loanId: string): Promise<LoanTransaction[]> => {
    const response = await api.get<LoanTransaction[]>(
      `/loans/${loanId}/transactions`
    );
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * Record a payment the provider never reported (202 Accepted, async).
   * `paymentType` picks the scenario: a pending direct debit that settled, or a
   * card payment made outside the system. Not reversible.
   */
  recordPayment: async (
    loanId: string,
    payload: RecordPaymentRequest
  ): Promise<void> => {
    await api.post(`/loans/${loanId}/record-payment`, payload);
  },

  /**
   * Delay a customer's next loan repayment
   * @param customerId - The customer ID
   * @param loanId - The loan ID
   * @param payload - Delay request (comment required, requestedDueDate optional for monthly)
   * @returns void (202 response, no body)
   */
  delayLoanPayment: async (
    customerId: string,
    loanId: string,
    payload: DelayLoanPaymentRequest
  ): Promise<void> => {
    await api.post(`/customers/${customerId}/loans/${loanId}/delay`, payload);
  },

  /**
   * Mark a loan as not collectable (202 Accepted, async). In addition to the
   * loan's advance status, this DISABLES the customer's account — removes the
   * repayment schedule, freezes interest, blocks notifications, and suspends
   * app access — while retaining the outstanding balance. Also clears any
   * internal-collections flag. Guide §7.1.
   */
  markLoanNotCollectable: async (
    loanId: string,
    reason: string
  ): Promise<void> => {
    await api.post(`/loans/${loanId}/not-collectable`, { reason });
  },

  /**
   * Move a loan into internal collections (202 Accepted, async) — retains the
   * debt in-house, cancels pending PowerPay debits. The customer is NOT
   * disabled. Guide §7.2.
   */
  sendLoanToInternalCollections: async (
    loanId: string,
    reason: string
  ): Promise<void> => {
    await api.post(`/loans/${loanId}/internal-collections`, { reason });
  },

  /**
   * Defer an advance's offer process (202 Accepted, async). While live, no
   * new offer tier is generated for this advance and the CUSTOMER's whole
   * collections handover is held — until `expiration` plus the configured
   * collections wait. Repayments, interest, and an already-sent offer are
   * unaffected; the advance's status does not change.
   *
   * `expiration` is the instant the deferral LAPSES (exclusive) and must be
   * in the future, with an explicit offset — the BE reads offsetless values
   * as AEST and a bare date as the START of that AEST day (see
   * features/advances/utils/deferOffer.ts). Re-posting is an upsert (shorten,
   * extend, or effectively cancel); there is no remove endpoint and the field
   * is never cleared. SCRUM-738 / BE SCRUM-1434.
   */
  deferOffer: async (
    loanId: string,
    expiration: string,
    reason: string
  ): Promise<void> => {
    await api.post(`/loans/${loanId}/defer-offer`, { expiration, reason });
  },

  /**
   * Send a customer's active loans to external collections / PPC (202
   * Accepted, async). Customer-level — refers ALL of the customer's active
   * loans, not just one advance — and takes no body. Returns 409 when already
   * in collections, no active loans, or the balance is below the $150 minimum;
   * surface that message verbatim. Guide §7.3.
   */
  sendCustomerToCollections: async (customerId: string): Promise<void> => {
    await api.post(`/customers/${customerId}/send-to-collections`);
  },

  /**
   * Reactivate a disabled customer (202 Accepted, async) — restores app access
   * only. Loans, schedules, offers and balance are unchanged, and other flags
   * (fraud, bankrupt, hardship…) are NOT cleared. Body is `{ reason }` alone:
   * no statusCode (single target state) and no actor (taken from the token).
   * 409 CUSTOMER_DECEASED is terminal; 409 CUSTOMER_NOT_DISABLED means the
   * screen is stale. SCRUM-1301.
   */
  reactivateCustomer: async (
    customerId: string,
    reason: string
  ): Promise<void> => {
    await api.post(`/customers/${customerId}/reactivate`, { reason });
  },

  /**
   * Void an account's login email (204, synchronous) so the address can be
   * given to another account — the shell-account swap in MPN-30. The BE moves
   * `userName` to `voidedUserName` and the address stops matching every
   * uniqueness check. Works on an account with no profile (a shell account)
   * and is refused once the account has completed KYC. Body is `{ reason }`
   * alone (mandatory); the actor comes from the token. There is no un-void
   * route. 409 ACCOUNT_KYC_VERIFIED is terminal; 409 EMAIL_ALREADY_VOIDED /
   * NO_LOGIN_EMAIL / EMAIL_CHANGED_DURING_VOID mean the screen is stale;
   * 404 AUTH_CREDENTIALS_NOT_FOUND means no such account.
   */
  voidCustomerEmail: async (
    customerId: string,
    reason: string
  ): Promise<void> => {
    await api.post(`/customers/${customerId}/void-email`, { reason });
  },

  /**
   * Clear the duplicate tag on a customer profile (MPN-95, BE PR #1309): sets
   * `duplicateOf` to null so `duplicate` reads false and the customer app
   * stops routing the customer to its dead-end duplicate screen. 204 No
   * Content, synchronous — a direct profile write plus a Duplicate/untagged
   * audit row carrying this reason and the previous original's client number.
   * Body is `{ reason }` alone (mandatory — 400 INVALID_REQUEST when blank);
   * the actor comes from the token. 409 NOT_DUPLICATE when the customer is
   * not tagged (the screen is stale), 404 CUSTOMER_NOT_FOUND. There is no
   * re-tag route, so this cannot be undone from the portal.
   */
  clearDuplicate: async (customerId: string, reason: string): Promise<void> => {
    await api.post(`/customers/${customerId}/clear-duplicate`, { reason });
  },

  /**
   * Apply or remove a staff flag (202 Accepted — the flip lands out-of-band on
   * the per-customer FIFO queue). `reason` is mandatory on all twelve routes
   * (400 otherwise); `expiry` is hardship/monitor only and must be in the
   * future; `type` is required on apply-bankrupt / apply-debt-agency-counsellor
   * and ignored on every remove. 409 CUSTOMER_FLAG_ALREADY_SET /
   * CUSTOMER_FLAG_NOT_SET mean the screen is stale; 409 PENDING_PAYMENTS_EXIST
   * (apply-hardship only) means retry once the pending debit settles.
   */
  setCustomerFlag: async (
    customerId: string,
    flag: CustomerFlag,
    action: CustomerFlagAction,
    payload: CustomerFlagPayload
  ): Promise<void> => {
    const slug = CUSTOMER_FLAG_SLUGS[flag][action];
    await api.post(`/customers/${customerId}/${slug}`, payload);
  },

  /**
   * Change when a customer's assessment blacklist ends, or lift it (MPN-15,
   * BE PR #1293). 204 No Content, synchronous — the profile reads back the
   * new pair on the next GET. `expiration` is required but nullable: an ISO
   * date-time no earlier than today AEST moves the end (today lifts it now),
   * `null` lifts it and empties the customer-facing reason. 409
   * ASSESSMENT_BLACKLIST_NOT_SET when the customer has no blacklist (the route
   * never creates one), 400 on a backdated day or a blank reason, 404
   * CUSTOMER_PROFILE_NOT_FOUND.
   */
  updateAssessmentBlacklist: async (
    customerId: string,
    payload: UpdateAssessmentBlacklistRequest
  ): Promise<void> => {
    await api.patch(`/customers/${customerId}/assessment-blacklist`, payload);
  },
};
